import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import {
  setCurrencyCode,
  setReportableCurrencies,
  resetReportableCurrencies,
  selectCurrencyLabel,
  HARDCODED_CURRENCIES,
  selectReportableCurrencies,
  selectCurrencyCode
} from 'features/settings/settingsSlice';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import DropDownWithCurrentSelection, {
  DropDownItemType
} from 'common/components/drop-downs/legacy/dropDownWithCurrentSelection';
import { useGetSiteCurrenciesQuery } from 'app/apis/queryApi';
import { useUpsertSiteCurrencyCodeConfigMutation } from 'app/apis/commandApi';
import { selectUserDetails } from 'features/auth/authenticationSlice';
import { useAuth } from 'features/auth/hooks/useAuth';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';

export const GeneralSettings: FC = () => {
  const dispatch = useDispatch();

  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const selectedReportableCurrencies = useSelector(selectReportableCurrencies);

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const userDetails = useSelector(selectUserDetails);
  const { isSuperUser } = useAuth();

  const {
    data: reportableFromApi,
    isLoading,
    isError
  } = useGetSiteCurrenciesQuery({ siteId });

  const [saveReportableCurrencies, { isLoading: isSaving }] =
    useUpsertSiteCurrencyCodeConfigMutation();

  const handleCheckboxChange = (currency: string) => {
    const updated = selectedReportableCurrencies.includes(currency)
      ? selectedReportableCurrencies.filter((c) => c !== currency)
      : [...selectedReportableCurrencies, currency];

    dispatch(setReportableCurrencies(updated));
  };

  const handleSave = async () => {
    try {
      await saveReportableCurrencies({
        siteId,
        reportableCurrencyCodes: selectedReportableCurrencies,
        updatedBy: userDetails.UserName
      }).unwrap();
      console.log('Reportable currencies saved');
    } catch (err) {
      console.error('Failed to save reportable currencies', err);
    }
  };

  const dropDownSource = reportableFromApi ?? selectedReportableCurrencies;

  const dropDownItems: DropDownItemType[] = dropDownSource.map((code) => ({
    item: code,
    label: selectCurrencyLabel(code)
  }));

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full min-h-1/2-screen grow basis-full">
        <section className="mx-6 my-6">
          <h2 className="text-primary-700 text-l leading-7 mb-1 mt-2 ml-2">
            Set Reporting Currency
          </h2>

          {isLoading && (
            <p className="ml-2 text-neutral-500">Loading currencies...</p>
          )}
          {isError && (
            <p className="ml-2 text-red-600">Failed to load currencies</p>
          )}

          {!isLoading && !isError && (
            <>
              <DropDownWithCurrentSelection
                dropDownItems={dropDownItems}
                currentSelectionLabel={selectedCurrencyCode}
                selectDropDownItem={(key) => dispatch(setCurrencyCode(key))}
              />

              {isSuperUser && (
                <>
                  <h2 className="text-primary-700 text-l leading-7 mb-1 mt-4 ml-2">
                    Allowed Reportable Currencies:
                  </h2>
                  <div className="grid grid-cols-2 gap-3 ml-2 mt-2 max-w-xl">
                    {HARDCODED_CURRENCIES.map((code) => (
                      <label key={code} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedReportableCurrencies.includes(code)}
                          onChange={() => handleCheckboxChange(code)}
                        />
                        <span>{selectCurrencyLabel(code)}</span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-4 ml-2">
                    <Button
                      theme={ButtonTheme.SUCCESS}
                      identifier="update-segmentation-config"
                      onClick={() => {
                        void handleSave();
                      }}
                      isLoading={isSaving}
                      isDisabled={isSaving}
                    >
                      <span>Update Allowed Currencies</span>
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
